import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import "./UserPanelPages.css";
import Navbar from "../Component/Common/Navbar";
import {
  connectRealtimeSocket,
  disconnectRealtimeSocket,
} from "../Component/ServerPackage/CompleteCPanel/utils/realtimeSocket";

/* ==============================
   API BASE HELPER
============================== */
const normalizeBase = (value) => String(value || "").trim().replace(/\/+$/, "");

const ensureApiBase = (value) => {
  const clean = normalizeBase(value);

  if (!clean) return "http://localhost:5000/api";

  return clean.endsWith("/api") ? clean : `${clean}/api`;
};

const API_BASE = ensureApiBase(
  process.env.REACT_APP_API_BASE || "http://localhost:5000"
);

/* ==============================
   AUTH KEYS
============================== */
const AUTH_TOKEN_KEYS = [
  "token",
  "authToken",
  "accessToken",
  "userToken",
  "jwtToken",
];

const AUTH_STORAGE_KEYS = [
  "token",
  "authToken",
  "accessToken",
  "userToken",
  "jwtToken",
  "isLoggedIn",
  "user",
  "currentUser",
  "name",
  "userName",
  "fullName",
  "username",
  "email",
  "userEmail",
  "user_email",
  "demoUserEmail",
];

/* ==============================
   AUTH HELPERS
============================== */
const getToken = () => {
  for (const key of AUTH_TOKEN_KEYS) {
    const localValue = localStorage.getItem(key);
    const sessionValue = sessionStorage.getItem(key);

    if (localValue && String(localValue).trim()) {
      return String(localValue).trim();
    }

    if (sessionValue && String(sessionValue).trim()) {
      return String(sessionValue).trim();
    }
  }

  return "";
};

const clearAuth = () => {
  AUTH_STORAGE_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });

  disconnectRealtimeSocket();
  window.dispatchEvent(new Event("auth-state-changed"));
};

/* ==============================
   FORMAT HELPERS
============================== */
const formatDateTime = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const normalizeActivity = (item = {}, index = 0) => ({
  id: item.id || item.activityId || item.activity_id || `activity-${index}`,
  title: item.title || "Activity",
  description:
    item.description ||
    item.desc ||
    item.message ||
    "No description available.",
  type:
    item.activity_type ||
    item.activityType ||
    item.type ||
    item.module ||
    "activity",
  time:
    item.timeFormatted ||
    item.createdAtFormatted ||
    item.created_at_formatted ||
    formatDateTime(item.created_at || item.createdAt || item.time),
});

const getActivityIcon = (type = "") => {
  const clean = String(type || "").toLowerCase();

  if (clean.includes("payment")) return "💳";
  if (clean.includes("domain")) return "🌐";
  if (clean.includes("hosting")) return "🚀";
  if (clean.includes("dns")) return "🧭";
  if (clean.includes("security")) return "🔐";
  if (clean.includes("order")) return "📦";
  if (clean.includes("favorite")) return "⭐";
  if (clean.includes("wallet")) return "👛";

  return "•";
};

function ActivityPage() {
  const navigate = useNavigate();

  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [realtimeStatus, setRealtimeStatus] = useState("connecting");
  const [status, setStatus] = useState({ type: "", message: "" });

  const redirectToLogin = useCallback(() => {
    clearAuth();
    navigate("/signin", { replace: true });
  }, [navigate]);

  /* ==============================
     PROTECTED FETCH
  ============================== */
  const authFetch = useCallback(
    async (endpoint, options = {}) => {
      const token = getToken();

      if (!token) {
        redirectToLogin();
        throw new Error("Session expired. Please login again.");
      }

      const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        cache: "no-store",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          ...(options.headers || {}),
        },
      });

      const data = await response.json().catch(() => ({}));

      if (response.status === 401 || response.status === 403) {
        redirectToLogin();
        throw new Error("Session expired. Please login again.");
      }

      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || "Request failed.");
      }

      return data;
    },
    [redirectToLogin]
  );

  /* ==============================
     LOAD ACTIVITY
  ============================== */
  const fetchActivities = useCallback(
    async (silent = false) => {
      try {
        if (!silent) {
          setLoading(true);
        } else {
          setRefreshing(true);
        }

        setStatus({ type: "", message: "" });

        const data = await authFetch("/activity/my-activity");

        const safeActivities = Array.isArray(data?.data)
          ? data.data.map(normalizeActivity)
          : Array.isArray(data?.activities)
          ? data.activities.map(normalizeActivity)
          : [];

        setActivities(safeActivities);
      } catch (err) {
        setActivities([]);

        setStatus({
          type: "error",
          message: err.message || "Failed to fetch activity.",
        });
      } finally {
        if (!silent) {
          setLoading(false);
        } else {
          setRefreshing(false);
        }
      }
    },
    [authFetch]
  );

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  /* ==============================
     REALTIME SOCKET
  ============================== */
  useEffect(() => {
    const socket = connectRealtimeSocket();

    if (!socket) {
      setRealtimeStatus("offline");
      return undefined;
    }

    let refreshTimer = null;

    const softRefresh = () => {
      clearTimeout(refreshTimer);

      refreshTimer = setTimeout(() => {
        fetchActivities(true);
      }, 400);
    };

    const handleConnected = () => {
      setRealtimeStatus("connected");
    };

    const handleDisconnected = () => {
      setRealtimeStatus("offline");
    };

    const handleActivityUpdate = (payload = {}) => {
      setRealtimeStatus("connected");

      setStatus({
        type: "success",
        message: payload?.message || "Activity updated successfully.",
      });

      softRefresh();
    };

    const handleRealtimeRefresh = () => {
      setRealtimeStatus("connected");
      softRefresh();
    };

    const handleSocketAuthError = () => {
      redirectToLogin();
    };

    socket.on("connect", handleConnected);
    socket.on("disconnect", handleDisconnected);
    socket.on("socket:connected", handleConnected);

    socket.on("activity:new", handleActivityUpdate);
    socket.on("activity:update", handleActivityUpdate);
    socket.on("activity:status", handleActivityUpdate);

    socket.on("payment:status", handleRealtimeRefresh);
    socket.on("domain:status", handleRealtimeRefresh);
    socket.on("hosting:status", handleRealtimeRefresh);
    socket.on("dashboard:update", handleRealtimeRefresh);
    socket.on("notification:new", handleRealtimeRefresh);
    socket.on("wallet:update", handleRealtimeRefresh);
    socket.on("wallet:status", handleRealtimeRefresh);

    socket.on("connect_error", handleSocketAuthError);

    if (socket.connected) {
      handleConnected();
    }

    return () => {
      clearTimeout(refreshTimer);

      socket.off("connect", handleConnected);
      socket.off("disconnect", handleDisconnected);
      socket.off("socket:connected", handleConnected);

      socket.off("activity:new", handleActivityUpdate);
      socket.off("activity:update", handleActivityUpdate);
      socket.off("activity:status", handleActivityUpdate);

      socket.off("payment:status", handleRealtimeRefresh);
      socket.off("domain:status", handleRealtimeRefresh);
      socket.off("hosting:status", handleRealtimeRefresh);
      socket.off("dashboard:update", handleRealtimeRefresh);
      socket.off("notification:new", handleRealtimeRefresh);
      socket.off("wallet:update", handleRealtimeRefresh);
      socket.off("wallet:status", handleRealtimeRefresh);

      socket.off("connect_error", handleSocketAuthError);

      disconnectRealtimeSocket();
    };
  }, [fetchActivities, redirectToLogin]);

  const handleRefresh = () => {
    fetchActivities(true);
  };

  return (
    <>
      <Navbar />

      <section className="user-page-section">
        <div className="user-page-container">
          <div className="user-page-head">
            <div>
              <h1>Recent Activity</h1>
              <p>Track your account activity in real time.</p>

              <div className="user-realtime-row">
                <div className="user-realtime-chip">
                  <span
                    className={
                      realtimeStatus === "connected"
                        ? "user-realtime-dot online"
                        : "user-realtime-dot offline"
                    }
                  />

                  <span>
                    {realtimeStatus === "connected"
                      ? "Realtime connected"
                      : realtimeStatus === "connecting"
                      ? "Connecting realtime..."
                      : "Realtime offline"}
                  </span>
                </div>
              </div>
            </div>

            <button
              type="button"
              className="user-page-refresh-btn"
              onClick={handleRefresh}
              disabled={loading || refreshing}
            >
              {refreshing ? "Refreshing..." : "Refresh"}
            </button>
          </div>

          {status.message ? (
            <div className={`user-page-alert ${status.type}`}>
              {status.message}
            </div>
          ) : null}

          <div className="user-page-card">
            {loading ? (
              <p className="user-page-muted">Loading activity...</p>
            ) : activities.length === 0 ? (
              <p className="user-page-muted">No recent activity found yet.</p>
            ) : (
              <div className="vertical-list">
                {activities.map((item) => (
                  <div className="list-row" key={item.id}>
                    <div className="activity-row-left">
                      <div className="activity-icon-box">
                        {getActivityIcon(item.type)}
                      </div>

                      <div>
                        <h3>{item.title}</h3>
                        <p>{item.description}</p>
                        <small className="list-type">{item.type}</small>
                      </div>
                    </div>

                    <span className="list-time">{item.time}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

export default ActivityPage;